<script setup>
import { ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useSupportStore } from '@/stores/support.store'
import MessagingThread from '@core/components/MessagingThread.vue'

// RM-212: in-place support chat. Opens the member↔support messages thread in a
// dialog over the current screen (the AI chat) so the member doesn't lose
// their conversation — previously every support entry point navigated to
// /messages.
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const { smAndDown } = useDisplay()
const supportStore = useSupportStore()

const supportConversationId = ref(null)
const loadError = ref(false)

watch(() => props.modelValue, async open => {
  if (!open) return
  loadError.value = false
  const conv = await supportStore.getOrCreateSupportConversation()
  if (conv?._id) {
    supportConversationId.value = conv._id
  } else {
    loadError.value = true
  }
})

function close() {
  emit('update:modelValue', false)
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    :fullscreen="smAndDown"
    :width="smAndDown ? undefined : 480"
    no-click-animation
    :retain-focus="false"
    scroll-strategy="none"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="support-chat-dialog d-flex flex-column">
      <VToolbar
        density="compact"
        color="transparent"
      >
        <VToolbarTitle class="text-subtitle-2 font-weight-bold">
          Support
        </VToolbarTitle>
        <VBtn
          icon
          variant="text"
          aria-label="Close support chat"
          @click="close"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VToolbar>

      <div class="support-chat-dialog__body flex-1">
        <MessagingThread
          v-if="supportConversationId && !loadError"
          :conversation-id="supportConversationId"
          @close="close"
        />
        <div
          v-else-if="loadError"
          class="d-flex flex-column align-center justify-center h-100 text-medium-emphasis pa-6"
        >
          <VIcon
            size="32"
            class="mb-2"
          >
            mdi-alert-circle-outline
          </VIcon>
          <span class="text-body-2">Couldn't open support chat. Please try again.</span>
        </div>
        <div
          v-else
          class="d-flex align-center justify-center h-100"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="32"
          />
        </div>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped>
.support-chat-dialog {
  height: 100%;
}

/* Desktop: contained dialog with a fixed working height so the thread
   scrolls inside it instead of growing the dialog. */
@media (min-width: 960px) {
  .support-chat-dialog {
    height: 80vh;
  }
}

.support-chat-dialog__body {
  min-height: 0;
  flex: 1;
}
</style>
